import React, { useRef, useEffect, useCallback } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { motion, useAnimate, type AnimationPlaybackControls } from 'motion/react';
import { GetWeavePopNotificationActionsByType, WeavePopNotification, NotificationActions } from '@frontend/types';
import { theme } from '@frontend/theme';
import { contextFactory } from '@frontend/design-system';
import { AI_RECEPTIONIST_STATE_BACKGROUND_GRADIENT } from '../grouped-notifications/filter-chips/constants';
import { useNotificationPersonalizationMap } from '../notification-personalization/notification-personalization';
import { useNewNotificationsContext } from '../shared/new-notifications-context';
import { NotificationsActions } from '../shared/notifications-actions-context';
import {
  DefaultCloseNotificationButton,
  DefaultNotificationActions,
  DefaultNotificationBodyWrapper,
  DefaultNotificationHeader,
  DefaultNotificationInnerWrapper,
} from './defaults/default-notification';

dayjs.extend(relativeTime);

type BaseNotificationOuterProps<N extends WeavePopNotification> = BaseNotificationComponentProps<N> & {
  children: React.ReactNode;
};

export type BaseNotificationComponentProps<N extends WeavePopNotification> = {
  isHistorical?: boolean;
  stacked?: boolean;
  notification: N;
  className?: string;
  isExperimentalMode?: boolean;
  emit: (event: GetWeavePopNotificationActionsByType<N['type']>, notification: N) => void;
  actions?: NotificationActions;
};

export const BaseNotificationOuter = <T extends WeavePopNotification>({
  children,
  isExperimentalMode = false,
  actions,
  ...props
}: BaseNotificationOuterProps<T>) => {
  const isNewNotificationsExperience = useNewNotificationsContext();

  if (isNewNotificationsExperience) {
    return (
      <BaseNotificationOuterNew actions={actions} {...props}>
        {children}
      </BaseNotificationOuterNew>
    );
  }

  if (isExperimentalMode) {
    return <BaseNotificationOuterExperimental {...props}>{children}</BaseNotificationOuterExperimental>;
  }

  return <BaseNotificationOuterDefault {...props}>{children}</BaseNotificationOuterDefault>;
};

export const [NotificationTypeContext, useNotificationType] = contextFactory<WeavePopNotification['type']>();

type AnimatedGradientColors = {
  outerBackground: string;
  animatedBackgrounds: [string, string];
};

const DEFAULT_NOTIFICATION_GRADIENT_COLORS: AnimatedGradientColors = {
  outerBackground: 'linear-gradient(92.94deg, #146ef5 48.5%, #c467de 100%)',
  animatedBackgrounds: [
    'linear-gradient(92.94deg, #DCEBFE 48.5%, #F7D8FF 100%)',
    'linear-gradient(92.94deg, #F7D8FF 5%, #DCEBFE 46%)',
  ],
};

const AI_RECEPTIONIST_NOTIFICATION_GRADIENT_COLORS: AnimatedGradientColors = {
  outerBackground: AI_RECEPTIONIST_STATE_BACKGROUND_GRADIENT,
  animatedBackgrounds: [AI_RECEPTIONIST_STATE_BACKGROUND_GRADIENT, 'linear-gradient(#E0F9F9, #DDEDFD)'],
};

export const BaseNotificationOuterNew = <T extends WeavePopNotification>({
  className,
  children,
  notification,
  actions,
  isHistorical,
}: BaseNotificationOuterProps<T>) => {
  const notificationPersonalizationMap = useNotificationPersonalizationMap();
  const { color } = notificationPersonalizationMap(notification.type);

  const viewAction = actions?.[0];
  const hasDefaultGradient = color?.background === 'gradient';
  const hasAiReceptionistGradient =
    notification.type === 'ai-receptionist-state' && color?.background === AI_RECEPTIONIST_STATE_BACKGROUND_GRADIENT;
  const hasGradient = !isHistorical && (hasDefaultGradient || hasAiReceptionistGradient);

  return (
    <NotificationsActions.Provider value={actions}>
      <NotificationTypeContext.Provider value={notification.type}>
        {hasGradient ? (
          <AnimatedGradientWrapper
            useDelayedFadeToWhite={hasAiReceptionistGradient}
            gradientColors={
              hasAiReceptionistGradient
                ? AI_RECEPTIONIST_NOTIFICATION_GRADIENT_COLORS
                : DEFAULT_NOTIFICATION_GRADIENT_COLORS
            }
            onClick={() => {
              viewAction?.action?.();
            }}
            className={className}
          >
            {children}
          </AnimatedGradientWrapper>
        ) : (
          <div
            onClick={() => {
              viewAction?.action?.();
            }}
            css={css`
              position: relative;
              background: ${color.background};
              border-radius: ${theme.borderRadius.medium};
              padding: ${theme.spacing(2)};
              border: 1px solid ${color.primary};
              transition: background 0.3s ease-in-out;

              :hover:not(:has(.notification-close-button:hover)) {
                background: ${color.hover};
              }

              cursor: pointer;
            `}
            className={className}
          >
            {children}
          </div>
        )}
      </NotificationTypeContext.Provider>
    </NotificationsActions.Provider>
  );
};

const AnimatedGradientWrapper = ({
  children,
  onClick,
  className,
  gradientColors = DEFAULT_NOTIFICATION_GRADIENT_COLORS,
  useDelayedFadeToWhite = false,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  gradientColors?: AnimatedGradientColors;
  useDelayedFadeToWhite?: boolean;
}) => {
  const [scope, animate] = useAnimate();
  const animationRef = useRef<any>(null);
  const isHoveredRef = useRef(false);

  useEffect(() => {
    const startInitialAnimation = async () => {
      if (useDelayedFadeToWhite) {
        animationRef.current = animate(
          '#notification-gradient-background',
          { opacity: 0 },
          { duration: 1, delay: 2.5, ease: 'easeInOut' }
        );

        await animationRef.current;
        return;
      }

      animationRef.current = animate(
        '#notification-gradient-background',
        { opacity: 0.5 },
        { duration: 0.5, delay: 2.5, ease: 'easeInOut' }
      );

      await animationRef.current;

      if (!isHoveredRef.current) {
        animate('#notification-gradient-background', { opacity: 0 }, { duration: 0.3, ease: 'easeInOut' });
      }
    };

    startInitialAnimation();
  }, [animate, useDelayedFadeToWhite]);

  const handleMouseEnter = useCallback(async () => {
    isHoveredRef.current = true;

    if (animationRef.current) {
      animationRef.current.stop();
    }

    animate('#notification-gradient-background', { opacity: 1 }, { duration: 0.3, ease: 'easeInOut' });
  }, [animate]);

  const handleMouseLeave = useCallback(async () => {
    isHoveredRef.current = false;

    if (animationRef.current) {
      animationRef.current.stop();
    }

    animate('#notification-gradient-background', { opacity: 0 }, { duration: 0.3, ease: 'easeInOut' });
  }, [animate]);

  return (
    <div
      ref={scope}
      onClick={() => {
        onClick?.();
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      css={css`
        position: relative;
        background: ${gradientColors.outerBackground};
        border-radius: ${theme.borderRadius.medium};
        transition: background 0.3s ease-in-out;
        padding: ${theme.spacing(2)};
        cursor: pointer;
      `}
      className={className}
    >
      <AnimatedGradientBackground gradientColors={gradientColors} />
      {children}
    </div>
  );
};

const animatedGradientBackgroundBaseStyles = css`
  position: absolute;
  inset: 1px;
  width: calc(100% - 2px);
  height: calc(100% - 2px);
  border-radius: 7px;
`;

const AnimatedGradientBackground = ({ gradientColors }: { gradientColors: AnimatedGradientColors }) => {
  const animationConfig = {
    background: gradientColors.animatedBackgrounds,
    transition: {
      background: {
        duration: 1,
        repeat: Infinity,
        times: [0, 1],
        type: 'tween',
        repeatType: 'reverse',
        ease: 'easeInOut',
      },
    },
  };

  return (
    <>
      <div
        css={[
          animatedGradientBackgroundBaseStyles,
          css`
            background: ${theme.colors.white};
          `,
        ]}
      />
      <motion.div
        id='notification-gradient-background'
        css={[
          animatedGradientBackgroundBaseStyles,
          css`
            transition: opacity 0.3s ease-in-out;
            opacity: 1;
          `,
        ]}
        animate={animationConfig}
      />
    </>
  );
};

export const BaseNotificationOuterExperimental = <T extends WeavePopNotification>({
  stacked,
  className,
  children,
}: BaseNotificationOuterProps<T>) => {
  return (
    <div
      css={css`
        position: relative;
        background: ${theme.colors.white};
        border-bottom-left-radius: ${theme.borderRadius.medium};
        border-bottom-right-radius: ${theme.borderRadius.medium};
        border-top-left-radius: ${stacked
          ? 0
          : theme.borderRadius.medium}; //when stacked this should fit snugly with the element above
        border-top-right-radius: ${stacked
          ? 0
          : theme.borderRadius.medium}; //when stacked this should fit snugly with the element above
      `}
      className={className}
    >
      {children}
    </div>
  );
};

export const BaseNotificationOuterDefault = <T extends WeavePopNotification>({
  isHistorical,
  notification,
  emit,
  stacked,
  className,
  children,
}: BaseNotificationOuterProps<T>) => {
  const [scope, animate] = useAnimate();
  const animation = useRef<AnimationPlaybackControls>();
  const hasTimeout = !!notification.state.timeout && !isHistorical;

  useEffect(() => {
    if (!hasTimeout) {
      return;
    }
    animation.current = animate(
      scope.current,
      {
        width: 0,
      },
      {
        duration: (notification.state.timeout ?? 0) / 1000,
      }
    );
    animation.current.then(() => {
      // don't call onTimeout if the animation has been canceled
      if (!animation.current) return;
      onTimeout();
    });
    return () => {
      animation.current?.cancel();
      animation.current = undefined;
    };
  }, [notification.id]);

  useEffect(() => {
    if (!hasTimeout) {
      return;
    }
    if (!notification.state.paused) {
      animation.current?.play();
    }
  }, [notification.state.paused, notification.state.timeout]);

  const onMouseEnter = () => {
    emit(
      {
        action: 'focus',
        payload: undefined,
      },
      notification
    );

    animation.current?.pause();
  };

  const onMouseLeave = () => {
    if (notification.state.paused) {
      return;
    }
    emit(
      {
        action: 'blur',
        payload: undefined,
      },
      notification
    );
    animation.current?.play();
  };

  const onTimeout = () => {
    emit({ action: 'timed-out', payload: undefined }, notification);
  };

  return (
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      css={css`
        position: relative;
        margin-bottom: ${theme.spacing(0.5)};
        background: ${theme.colors.white};
        border-bottom-left-radius: ${theme.borderRadius.medium};
        border-bottom-right-radius: ${theme.borderRadius.medium};
        border-top-left-radius: ${stacked
          ? 0
          : theme.borderRadius.medium}; //when stacked this should fit snugly with the element above
        border-top-right-radius: ${stacked
          ? 0
          : theme.borderRadius.medium}; //when stacked this should fit snugly with the element above
        box-shadow: ${stacked ? 'none' : theme.shadows.heavy}; //when stacked the shadow goes on the wrapper
        width: 350px;
      `}
      className={className}
    >
      {children}
      {hasTimeout && (
        <motion.div
          ref={scope}
          /**
           * We set the styles such that there is something to animate, but the user cannot see it.
           * This allows us to rely solely on framer to handle the animation and auto-dismissal of the notification.
           */
          css={css`
            height: 2px;
            background: transparent;
          `}
          initial={{ width: '100%' }}
        />
      )}
    </div>
  );
};

export type NotificationInnerProps = {
  actions: NotificationActions;
  title: React.ReactNode;
  body: React.ReactNode;
  location?: string;
  timestamp: string | number;
  stacked?: boolean;
  isUnread?: boolean;
  id: string;
  onClose?: (e: React.MouseEvent) => void;
  notificationType: string;
};

export const BaseNotificationDefault = ({
  actions,
  title,
  body,
  timestamp,
  stacked,
  onClose,
  notificationType,
}: NotificationInnerProps) => {
  return (
    <div css={{ position: 'relative' }}>
      {!!onClose && <DefaultCloseNotificationButton onClick={onClose} />}
      <DefaultNotificationHeader title={title} timestamp={timestamp} stacked={stacked ?? false} />
      <DefaultNotificationInnerWrapper hasActions={!!actions.length}>
        <DefaultNotificationBodyWrapper>{body}</DefaultNotificationBodyWrapper>
        {actions.length ? <DefaultNotificationActions actions={actions} notificationType={notificationType} /> : null}
      </DefaultNotificationInnerWrapper>
    </div>
  );
};

export const BaseNotificationInner = ({
  actions,
  title,
  body,
  timestamp,
  stacked,
  onClose,
  notificationType,
  id,
}: NotificationInnerProps) => {
  const isNewNotificationsExperience = useNewNotificationsContext();

  if (!isNewNotificationsExperience) {
    return (
      <BaseNotificationDefault
        actions={actions}
        title={title}
        body={body}
        timestamp={timestamp}
        id={id}
        stacked={stacked}
        onClose={onClose}
        notificationType={notificationType}
      />
    );
  }
  return (
    <BaseNotificationInnerNew
      actions={actions}
      title={title}
      body={body}
      timestamp={timestamp}
      stacked={stacked}
      id={id}
      onClose={onClose}
      notificationType={notificationType}
    />
  );
};

export const BaseNotificationInnerNew = ({
  actions,
  title,
  body,
  onClose,
  notificationType,
  timestamp,
}: NotificationInnerProps) => {
  const otherActions = actions.filter((_, index) => index > 0);
  return (
    <div css={{ position: 'relative' }}>
      <DefaultNotificationHeader timestamp={timestamp} onClose={onClose} title={title} />
      <DefaultNotificationInnerWrapper hasActions={false}>
        <DefaultNotificationBodyWrapper>{body}</DefaultNotificationBodyWrapper>
        {!!otherActions.length ? (
          <DefaultNotificationActions actions={otherActions} notificationType={notificationType} />
        ) : null}
      </DefaultNotificationInnerWrapper>
    </div>
  );
};
